/**
 * riskAnchors.js — plain-language question + five concrete option descriptions
 * for each of the 26 lite risk-answer keys (see liteQuestions.js RISK_QUESTIONS).
 *
 * Source of truth for the wording is Jim's MVA workbook template, extracted to
 * jim_anchors.txt (50 criteria with behavioural anchors per 0..4 level). Where a
 * workbook criterion maps cleanly onto a lite key, its thresholds are kept
 * verbatim (only reworded for plain-English readability) and `source: 'workbook'`.
 * Where no clean match exists, the option set is written from scratch in the
 * same style, anchored to the intent described in liteQuestions.js `help` text,
 * and marked `source: 'adapted'`.
 *
 * INVARIANT: options are always ordered 0 (weakest business) -> 4 (strongest
 * business), matching the workbook's 0..4 scale (HIGHER = STRONGER). Getting
 * this backwards silently corrupts every valuation that uses it — see
 * riskAnchors.test.js.
 */

export const RISK_ANCHORS = {
  // ---------------------------------------------------------------------
  // VAC-derived (14)
  // ---------------------------------------------------------------------

  ownerDependency: {
    question: 'How much time does the business need from you each week?',
    options: [
      'More than 50 hours a week',
      'About 30 to 50 hours a week',
      'About 10 to 30 hours a week',
      'About 5 to 10 hours a week',
      'Under 5 hours a week — it runs without me',
    ],
    // adapted: every rung was open-ended ("More than X"), so an owner
    // working 60 hours truthfully matched four of the five options and
    // correct scoring depended on them happening to pick the first true
    // statement rather than the tightest one. Converted to closed bands.
    source: 'adapted',
  },

  customerConcentration: {
    question: "What share of your revenue comes from your single biggest customer?",
    options: [
      'More than half of my revenue comes from one customer',
      'Between 30% and 50% comes from one customer',
      'Between 20% and 30% comes from one customer',
      'Between 5% and 20% comes from one customer',
      'No single customer is more than 5% of revenue',
    ],
    // adapted: same open-ended-rung defect as ownerDependency — a business
    // with 60% on one customer matched four of the five options, and the
    // score depended on which true statement the owner picked first.
    // Converted to closed bands.
    source: 'adapted',
  },

  revenueGrowthTrend: {
    question: 'How has your revenue trended over the last few years?',
    options: [
      'Declining sharply — revenue has dropped noticeably in recent years',
      'Declining, or irregular with no real growth — swinging up and down unpredictably',
      'Flat — about the same each year',
      'Growing moderately — roughly 5% to 10% a year',
      'Growing strongly — more than 10% a year',
    ],
    // adapted: the workbook-sourced ladder had no declining option at all —
    // all 5 rungs ran from "irregular" up through three growth tiers — even
    // though liteQuestions.js help text explicitly anchors this scale at
    // "0 = declining sharply, 2 = flat, 4 = growing strongly". An owner with
    // shrinking revenue had nothing accurate to pick. Rebuilt the ladder
    // around those three literal anchor points.
    source: 'adapted',
  },

  marginTrend: {
    question: 'How have your profit margins trended over the last few years?',
    options: [
      'Shrinking fast — keeping less profit on each sale every year',
      'Slipping — margins are gradually getting worse',
      'Holding steady — about the same margin each year',
      'Improving — margins are gradually getting better',
      'Expanding strongly — margins are clearly better than a few years ago',
    ],
    source: 'adapted', // no clean margin-trend criterion in the dump; mirrors Financial Trends idiom
  },

  keyPersonRisk: {
    question: 'If the most important person in the business besides you stopped working tomorrow, what would happen?',
    options: [
      "There is no one else — I'm the only person who can do the critical work",
      'The business would be in serious trouble — that person could not be replaced',
      'Major disruption for months while we found and trained a replacement',
      'A difficult stretch, but others could cover while we replaced them',
      'Barely noticeable — the work is shared and written down, so someone else picks it up',
    ],
    // adapted (2nd pass): the ladder had no rung for a solo owner or an
    // owner-plus-contractors business — there is no "most important person
    // besides me", so those owners had to pick "Barely noticeable" = 4. This
    // key feeds the engine's single-point-of-failure slot, so the most
    // exposed business on the platform was scoring maximum. Added an
    // explicit worst-case rung at 0 and shifted the rest up.
    //
    // adapted (1st pass): the workbook's Key Person Dependency ladder ("4+ key people" = 0
    // ... "no dependency" = 4) contradicts its own explanation, which says a
    // single irreplaceable person with no contract should score 0. Counting
    // heads also let an owner with one indispensable, un-backed-up person pick
    // an option that read as "flagging a risk" while scoring 3/4 — nearly
    // ideal. Measuring the consequence of that person leaving directly
    // sidesteps the workbook's internal contradiction and matches the
    // wizard's stated intent (liteQuestions.js help text: "0 = severe
    // disruption, 4 = barely noticeable").
    source: 'adapted',
  },

  processDocumentation: {
    question: 'How well are your core day-to-day processes written down?',
    options: [
      "Not written down — it's all in people's heads",
      "A few processes are written down, but most aren't",
      'Main processes are documented, but not consistently followed',
      'Most processes are documented and followed',
      'Fully documented, kept up to date, and used to train new staff',
    ],
    source: 'adapted', // no dedicated process-documentation criterion in the dump
  },

  competitivePosition: {
    question: 'Compared with your direct competitors, what makes customers choose you?',
    options: [
      'We compete mainly on price — nothing much sets us apart',
      'Much like everyone else — customers could switch to a competitor without missing anything',
      'Some advantages — our reputation or service keeps most customers with us',
      "Clearly different — customers choose us for something competitors can't match",
      'The clear market leader — the business others in our market compare themselves to',
    ],
    // adapted (2nd pass): the previous rewrite framed this as a trend
    // ("losing ground" ... "gaining ground"), which made it a near-duplicate
    // of marketShareTrend — both keys opened on "losing ground to
    // competitors", so one business fact was counted twice. Split the two:
    // marketShareTrend keeps the trend, this key now measures standing and
    // differentiation at a point in time, which is what a buyer prices.
    //
    // adapted (1st pass): the workbook-sourced ladder counted the number of
    // direct competitors (a static, structural measure), while
    // liteQuestions.js help text frames this key as competitive standing
    // ("0 = losing ground, 4 = clear market leader locally"). A business
    // facing many competitors can still be strongly differentiated, and one
    // facing zero competitors can still be undifferentiated — competitor
    // count doesn't track the axis the score is meant to measure.
    source: 'adapted',
  },

  recurringRevenuePercent: {
    question: 'What share of your revenue is contracted or reliably repeats, rather than one-off sales?',
    options: [
      'None — every sale is one-off, and each new customer has to be won from scratch',
      'A little — under about a third of revenue comes from repeat or contracted customers',
      'Some — roughly a third to half of revenue repeats or is under contract',
      'Most — more than half, up to about 80%, is contracted or reliably repeats',
      'Nearly all — more than about 80% is contracted or recurring, and customers rarely leave',
    ],
    // adapted: the workbook-sourced bands left a dead zone — rung 1 was
    // "some repeat, nothing formal" and rung 2 jumped straight to "60% or
    // more", so a landscaper with 40% of revenue on annual contracts fit
    // neither and had to guess. Rebuilt as contiguous bands (none / under a
    // third / a third to half / half to 80% / over 80% with low churn) so
    // every share of recurring revenue lands on exactly one rung. Note this
    // closes the old 50-60% gap as well. Rung 3 is explicitly capped at
    // ~80%: leaving it open-ended meant a business at 90% with low churn
    // matched rungs 3 and 4 equally, the same multi-rung ambiguity fixed on
    // ownerDependency and customerConcentration.
    source: 'adapted',
  },

  managementDepth: {
    question: 'How strong is the management team below you?',
    options: [
      'Just me — no one else manages anything, no matter how many people work here',
      'One or two people help lead, but there is no real management layer',
      'An average team — some managers, but not a complete leadership group',
      'A strong management and operations team that could run daily operations without you',
      'A top-tier management and operations team that could grow the business without you',
    ],
    // adapted: the workbook-sourced ladder gated its two weakest rungs on
    // total headcount ("1 to 3 people total", "under 5 people"), leaving no
    // accurate option for a common SMB profile — e.g. 15 employees with the
    // owner directly overseeing everyone and zero management layer. Reworded
    // to measure management-layer presence directly, independent of
    // headcount, matching help text ("strength of the team below the
    // owner").
    source: 'adapted',
  },

  successionPlan: {
    question: 'How much planning is there for someone else to take over running the business?',
    options: [
      'No plan at all — the business could not keep running without me, even briefly',
      "No formal plan yet, though we know it's needed",
      'Some informal planning has started',
      "There's a plan and parts of it are already in place",
      "An extensive plan that's fully in place and tested",
    ],
    // adapted: the workbook-sourced rungs 0 and 1 were both restatements of
    // "no transition plan" ("there's no transition plan at all" vs. "no
    // transition plan yet"), giving an owner no real way to distinguish the
    // two weakest options. Reworded rung 0 as total dependency (no plan and
    // no one could step in) versus rung 1 as awareness without a plan yet.
    source: 'adapted',
  },

  contractQuality: {
    question: 'Are your customer, supplier and employee relationships covered by written contracts that would carry over to a new owner?',
    options: [
      'No written contracts — everything is a handshake deal',
      'A few written contracts, but most are old, unsigned or missing',
      "Customers, suppliers and employees are mostly covered, but the paperwork isn't kept current",
      "Contracts are current and easy to find, but I'm not sure they would carry over to a new owner",
      'Contracts are current, kept in one place, and written to transfer to a new owner on a sale',
    ],
    // adapted: liteQuestions.js help text turns on the word *transferable*
    // ("Are customer relationships under written, transferable contracts?
    // ... 4 = strong transferable contracts") and not one option mentioned
    // transfer. A business whose contracts are current, centralised and all
    // void on change of control scored 4 while being weak on precisely what
    // a buyer is paying for. Rung 4 now requires transfer on sale; rung 3
    // holds the common "current, but I don't know if they transfer" case.
    source: 'adapted',
  },

  systemsTechMaturity: {
    question: 'How well do the systems you run the business on actually work together?',
    options: [
      'Paper, memory and phone calls — almost nothing lives in a system',
      'Spreadsheets and email — the information exists, but nothing is joined up',
      'We have proper software for our trade (job or field management, practice management, accounting), but only parts of the team use it',
      'The main software is used across the business, though we still re-enter the same information in more than one place',
      'Our systems talk to each other — one source of truth, little double entry, and reports come straight out of the system',
    ],
    // adapted: the workbook-sourced ladder was a tool inventory ("a CRM" /
    // "a CRM and an ERP" / "a full stack"), which scores purchases rather
    // than capability. A well-run trades business on a single strong
    // field-management package under-picked because it owns no "ERP", while
    // a firm that bought a CRM nobody logs into scored 3. Re-anchored on
    // adoption and integration, in the software language these industries
    // actually use.
    source: 'adapted',
  },

  regulatoryExposure: {
    question: "How would you describe your business's compliance with the licences, permits and rules that apply to it?",
    options: [
      'Currently out of compliance, or dealing with an active regulatory or licensing problem',
      "Compliant, but it's a stretch — audits, renewals or inspections often catch us off guard",
      'Compliant, with regular effort needed to keep on top of requirements',
      'Compliant and well-managed, with low risk of ever falling behind',
      'Fully compliant with a clean track record, or few meaningful requirements apply to us',
    ],
    // adapted: the workbook-sourced ladder duplicated industryRegulatoryEnvironment
    // below (amount of regulation the industry faces) instead of measuring
    // the business's own compliance status, which is what liteQuestions.js
    // help text asks for ("0 = at-risk or non-compliant, 4 = fully compliant
    // with low exposure"). A heavily regulated but fully compliant business
    // would have scored as if at risk, and a lightly regulated but
    // non-compliant one would have scored as if safe. Rewrote to measure
    // compliance status directly.
    source: 'adapted',
  },

  marketShareTrend: {
    question: 'How has your share of the local market changed over the last few years?',
    options: [
      'Shrinking — losing ground to competitors',
      'Slipping slightly',
      'Holding steady',
      'Growing slightly',
      'Growing clearly — gaining ground on competitors',
    ],
    source: 'adapted', // no dedicated market-share-trend criterion in the dump
  },

  // ---------------------------------------------------------------------
  // Company Risk (5)
  // ---------------------------------------------------------------------

  arAgingQuality: {
    question: 'How quickly do your customers pay their invoices?',
    options: [
      'Chronically late — significant bad debt or write-offs',
      'Often late, with some collection issues',
      'Sometimes late, but manageable',
      'Mostly on time',
      "Consistently on time, with rarely any collection issues — or we're paid at the time of sale",
    ],
    source: 'adapted', // no dedicated receivables-aging criterion in the dump
  },

  cashFlowPredictability: {
    question: 'How predictable is your cash flow from month to month?',
    options: [
      "Very unpredictable — some months are strong, others barely cover the bills, and I can't tell in advance",
      'Hard to predict — big swings depending on which jobs land and when customers pay',
      'Somewhat predictable — a normal month is roughly what I expect, with occasional surprises',
      'Predictable — I can forecast most months within a comfortable margin',
      'Very predictable — contracted or recurring revenue means cash arrives on a steady, known schedule',
    ],
    // adapted: the workbook-sourced ladder was a payment-model taxonomy (long
    // terms vs. deposit-on-completion vs. cash-and-carry vs. recurring), not a
    // predictability scale. A firm on monthly contracts billed net-30 matched
    // rung 0 ("customers pay on long terms after the job's done") AND rung 4
    // ("contracted and recurring") equally well — a 4-point spread on a single
    // business. Cash-and-carry, meanwhile, was pinned mid-scale even though a
    // cash business has highly predictable collections. This key feeds two
    // engine slots (Budget & forecasting, Cash reserves), so the error was
    // double-weighted. Rewrote to measure forecastability directly, with the
    // payment model demoted to an example inside the top rung.
    source: 'adapted',
  },

  ipPortfolio: {
    question: 'Do you have trademarks, patents, proprietary processes, or a protected brand?',
    options: [
      'None of these',
      "An informal proprietary method or brand, but nothing formally protected",
      'One registered trademark or similar protection',
      'Multiple registered trademarks, patents, or clearly proprietary processes',
      "A strong, actively protected portfolio (patents, trademarks, and/or proprietary methods that are hard to copy)",
    ],
    source: 'adapted', // no dedicated IP criterion in the dump
  },

  litigationHistory: {
    question: 'Have there been any legal disputes involving the business in the last 5 years?',
    options: [
      'Yes — an ongoing or serious lawsuit',
      'Yes — a significant dispute that has since been resolved',
      'Yes — a few minor disputes, all resolved',
      'No disputes, though a claim or complaint was made and dropped',
      'No legal disputes at all — a clean record',
    ],
    source: 'adapted', // no dedicated litigation criterion in the dump
  },

  boardGovernance: {
    question: 'How often do you actually look at the numbers, and how?',
    options: [
      'Once a year — we look at the numbers when the accountant does the year-end',
      'A few times a year — we check in on the numbers roughly once a quarter',
      'Every month — we review financial reports on a regular monthly rhythm',
      'Monthly reviews plus a working budget — we track actuals against a budget and forecast ahead',
      'Real-time — a live financial dashboard shows where we stand at any moment, not just at review time',
    ],
    // source-changed: this key is routed by backend/services/liteRiskDerivation.js
    // (operationalFinancialControls, "Financial controls & reporting") to the
    // engine slot for financial-reporting cadence, not governance structure.
    // The previous anchors measured board/advisor structure instead, which
    // left reporting discipline — the thing the engine actually scores here —
    // completely unmeasured. Product owner ruling: match the engine, not the
    // old question. Restored to Jim's workbook criterion for this slot
    // ("Financial Controls & Reporting": annual review with the accountant →
    // quarterly → monthly → budget & forecast → live dashboard), reworded to
    // plain English.
    source: 'workbook',
  },

  // ---------------------------------------------------------------------
  // Industry Risk (7)
  // ---------------------------------------------------------------------

  industryRegulatoryEnvironment: {
    question: 'Is regulation in your industry working for you or against you?',
    options: [
      'Working against us — rules are tightening in ways that make it harder to operate',
      'Getting tougher — new requirements keep adding cost or restricting what we can do',
      'Unsettled — the rules change often enough that we have to keep watching them',
      'Stable — the rules are settled, or there are few of them, and we know what to expect',
      'Stable and in our favour — licensing sets a clear standard and keeps casual competitors out',
    ],
    // adapted: liteQuestions.js help text asks about direction and
    // friendliness ("How friendly is regulation to your industry? 0 =
    // hostile/tightening, 4 = stable and supportive") but the anchors
    // measured sheer intensity, which is a different thing and often the
    // opposite one. Licensed trades and professional practices — the core
    // market here: electricians, plumbers, HVAC, pharmacies, clinics — face
    // heavy but stable and supportive regulation that raises transfer value
    // by limiting who can compete, yet they scored 0 or 1. The old rungs 3
    // and 4 ("little to no regulation" / "no regulatory body at all") were
    // also indistinguishable in practice. Re-anchored on direction and
    // stability.
    source: 'adapted',
  },

  industryEnvironmentalExposure: {
    question: 'How much environmental risk does a business like yours carry?',
    options: [
      'High exposure — contamination, spills or emissions are a real risk in this business',
      'Significant exposure — we handle fuels, chemicals or waste that could cause a problem',
      'Moderate exposure — environmental rules apply to us, and a mistake would be costly',
      'Low exposure — minor things like vehicle emissions or waste disposal, nothing more',
      'None — nothing this business does creates a meaningful environmental risk',
    ],
    // adapted: the workbook-sourced ladder measured how desirable the
    // industry's environmental impact is, not the liability the buyer
    // inherits — which is what liteQuestions.js help text asks for
    // ("Exposure to environmental liabilities or climate impact. 0 = high
    // exposure, 4 = minimal"). The mismatch cut both ways: an accounting or
    // software firm has zero exposure but topped out at "Neutral" = 2, a
    // systematic under-score for most service businesses; a recycler scored
    // 4 while carrying real contamination liability. Re-anchored on exposure.
    source: 'adapted',
  },

  industryBusinessModelRobustness: {
    question: 'How durable is the way your industry makes money — is it under threat from new technology or new competitors?',
    options: [
      'Being disrupted now — technology or new competitors are already replacing how this industry works',
      'Under threat — change is coming and the old way of doing business is weakening',
      'Stable, but not protected — nothing would stop a new model from taking hold',
      "Durable — the way this industry works has held up well and isn't easy to replace",
      "Very durable — customers will keep needing this, and there's no realistic substitute",
    ],
    // adapted: the workbook-sourced ladder changed axis halfway up — rungs 0
    // and 1 asked about disruption exposure, rungs 2 to 4 switched to
    // industry-wide recurring-revenue percentages that no owner could know
    // (they were also a near-verbatim duplicate of recurringRevenuePercent,
    // double-counting one business fact across two keys). liteQuestions.js
    // help text asks a single question — "is the way this industry makes
    // money under threat (disruption, substitution)? 0 = being disrupted,
    // 4 = very durable" — so the ladder now runs on that one axis end to end
    // and asks only about things an owner can observe.
    source: 'adapted',
  },

  industryGrowthTrend: {
    question: "What's the growth trajectory of your industry as a whole?",
    options: [
      'Shrinking quickly',
      'Shrinking',
      'Flat — not growing or shrinking',
      'Growing',
      'Growing rapidly',
    ],
    source: 'workbook', // Industry Growth Trend
  },

  industryMarketSentiment: {
    question: 'How eager are buyers and lenders when it comes to businesses in your industry?',
    options: [
      'Avoided — buyers and lenders steer clear of this industry',
      'Hard to sell — few buyers, and financing is difficult',
      'Average — businesses like mine sell, but nothing special',
      'In demand — buyers actively look for businesses in this industry',
      'Highly sought after — buyers compete for businesses like mine',
    ],
    // adapted: this key and industryDemandStability were cross-wired — each
    // carried the other's anchors. Here the label ("Industry: buyer
    // sentiment"), the help text ("How eager are buyers/investors ... 0 =
    // avoided, 4 = highly sought after") and the engine slot all agree on
    // buyer/investor appetite, while the anchors measured general public
    // reputation. An industry can have a poor public image yet strong buyer
    // appetite (payday lending) or the reverse (independent bookshops), so
    // the two are not interchangeable. Rewrote the anchors onto buyer
    // appetite rather than bending the help text, since three of four
    // signals already pointed there.
    source: 'adapted',
  },

  industryDemandStability: {
    question: 'How steady is demand for what you sell when the economy turns down?',
    options: [
      'Very cyclical — demand collapses when the economy turns down',
      'Cyclical — noticeably slower in a downturn',
      'Somewhat steady — a downturn hurts, but people still buy',
      'Steady — demand holds up in good times and bad',
      'Recession-resistant — customers need this regardless of the economy',
    ],
    // adapted: this key and industryMarketSentiment were cross-wired. The
    // label ("Industry: demand stability"), the help text ("How steady is
    // customer demand through economic cycles? 0 = highly cyclical, 4 =
    // recession-resistant") and the engine slot all describe
    // recession-resistance, but the anchors ran "very low demand" to "very
    // high demand" — a different axis, and one that inverts for exactly the
    // trades this wizard targets. Construction, HVAC and restaurants are
    // high-demand AND highly cyclical: on the old ladder they scored 3 or 4
    // on a scale whose 0 means "highly cyclical". Rewrote onto cyclicality.
    source: 'adapted',
  },

  industryLifecycleStage: {
    question: 'Where is your industry in its overall lifecycle?',
    options: [
      'At the end of its lifecycle — declining and being phased out',
      'In decline',
      'Mature — the middle of its lifecycle',
      'Still emerging — an early, unproven stage with real upside but real uncertainty',
      'In a growth stage — established and still expanding, with plenty of room to run',
    ],
    // adapted: the workbook-sourced ladder ranked "still emerging" above "in
    // a growth stage" (index 4 vs. 3), but liteQuestions.js help text pins
    // index 4 to "early growth" — early in an established growth phase, not
    // a pre-growth/embryonic stage. For a Main Street business, an unproven
    // emerging industry is generally riskier (harder to finance, thinner
    // buyer pool) than an established, still-expanding one. Swapped the two
    // so growth stage ranks strongest.
    source: 'adapted',
  },
};

export default RISK_ANCHORS;
